import React, { useState } from "react";
import { X, Save, KeyRound, Camera, Eye, EyeOff, Mail } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { PhoneInput, isValidPhone, toPhoneE164 } from "./UI";

export function ProfileModal({ isOpen, onClose }) {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(toPhoneE164(user?.phone || ""));
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [emailNotif, setEmailNotif] = useState(user?.email_notif !== false);

  if (!isOpen) return null;

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 700 * 1024) {
      setError("Ukuran gambar maksimal 700KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAvatar(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    // No. HP opsional di sini, tapi kalau diisi harus format telepon yang sah.
    if (phone && !isValidPhone(phone)) {
      setError("No. HP tidak valid. Masukkan minimal 8 angka setelah +62.");
      return;
    }
    setLoading(true);
    try {
      await api.patch("/users/me/profile", { name, phone, avatar, email_notif: emailNotif });
      setSuccess("Profil berhasil diperbarui");
      // Reload user data in context
      await refresh();
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-[scaleIn_0.2s_ease-out]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800">Ubah Profil</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 rounded-lg p-1 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg">{error}</div>}
          {success && <div className="p-3 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg">{success}</div>}
          
          <div className="flex flex-col items-center pb-2">
            <div className="relative group">
              {avatar ? (
                <img src={avatar} alt="Avatar" className="w-20 h-20 rounded-full object-cover border border-slate-200 shadow-sm" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#045498] text-white flex items-center justify-center font-display font-semibold text-2xl shadow-sm">
                  {name?.[0]?.toUpperCase() || "U"}
                </div>
              )}
              <label className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Camera className="w-6 h-6 text-white" />
                <input type="file" className="hidden" accept="image/*" onChange={handleAvatarChange} />
              </label>
            </div>
            <div className="text-xs text-slate-500 mt-2">Klik foto untuk ubah (Maks 700KB)</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={user?.email || "-"}
              readOnly
              disabled
              className="kedan-input w-full !bg-slate-50 !text-slate-500 cursor-not-allowed"
              data-testid="profile-modal-email"
            />
            <p className="text-xs text-slate-400 mt-1">
              Email dipakai untuk login dan hanya dapat diubah oleh admin.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nama Lengkap</label>
            <input type="text" required className="kedan-input w-full" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">No. HP</label>
            <PhoneInput value={phone} onChange={setPhone} testid="profile-modal-phone" />
            <p className="text-xs text-slate-400 mt-1">Hanya angka, tanpa 0 di depan. Disimpan sebagai +62…</p>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/60 cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={emailNotif}
              onChange={(e) => setEmailNotif(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#045498] shrink-0"
              data-testid="profile-modal-email-notif"
            />
            <span>
              <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                <Mail className="w-4 h-4 text-slate-400" /> Notifikasi Email
              </span>
              <span className="block text-xs text-slate-400 mt-0.5">
                Kirim salinan notifikasi ke email di atas setiap ada aktivitas baru.
              </span>
            </span>
          </label>
          <div className="pt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="kedan-btn-secondary">Batal</button>
            <button type="submit" disabled={loading} className="kedan-btn-primary">
              <Save className="w-4 h-4" /> {loading ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PasswordModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (newPassword !== confirmPassword) {
      setError("Konfirmasi password baru tidak cocok");
      return;
    }
    setLoading(true);
    try {
      await api.patch("/users/me/password", {
        old_password: oldPassword,
        new_password: newPassword
      });
      setSuccess("Password berhasil diubah");
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-[scaleIn_0.2s_ease-out]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800">Ganti Password</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 rounded-lg p-1 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg">{error}</div>}
          {success && <div className="p-3 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg">{success}</div>}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password Lama</label>
            <div className="relative">
              <input 
                type={showOldPassword ? "text" : "password"} 
                required 
                className="kedan-input w-full pr-10" 
                value={oldPassword} 
                onChange={e => setOldPassword(e.target.value)} 
              />
              <button 
                type="button" 
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password Baru</label>
            <div className="relative">
              <input 
                type={showNewPassword ? "text" : "password"} 
                required 
                className="kedan-input w-full pr-10" 
                value={newPassword} 
                onChange={e => setNewPassword(e.target.value)} 
              />
              <button 
                type="button" 
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Konfirmasi Password Baru</label>
            <div className="relative">
              <input 
                type={showConfirmPassword ? "text" : "password"} 
                required 
                className="kedan-input w-full pr-10" 
                value={confirmPassword} 
                onChange={e => setConfirmPassword(e.target.value)} 
              />
              <button 
                type="button" 
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="pt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="kedan-btn-secondary">Batal</button>
            <button type="submit" disabled={loading} className="kedan-btn-primary">
              <KeyRound className="w-4 h-4" /> {loading ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
